// packages/core/src/learning/school-modules/relationship-family-money.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Relationships & family money". surface: 'school',
// homeSchool: 'relationship-family-money'. module id
// 'school-relationship-family-money' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #e89a4d) — flag for the presentation-tuning pass.
// tier/pillar: relationship/family money is a life-decision lens (shared
// goals, caregiving, boundaries) → tier 'life-design', pillar 'life-decisions'
// (core module 11, Big Life Decisions). Same mapping as the sibling
// real-estate-housing school; both diverge from the income-mapped members of
// the 'income-life-domains' bucket because they are life decisions, not
// earning power.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_RELATIONSHIP_FAMILY_MONEY_MODULE: LearningModule = {
	id: 'school-relationship-family-money',
	title: 'Relationships & family money',
	description:
		'The lens that treats money as relational — building trust, naming family-support boundaries, and finding shared language before shared accounts, without shaming love or obligation.',
	emoji: '🤝', // PLACEHOLDER — tune in presentation pass
	accentColor: '#e89a4d', // from SchoolMeta color — tune in presentation pass
	tier: 'life-design',
	pillar: 'life-decisions',
	order: 0,
	surface: 'school',
	homeSchool: 'relationship-family-money',
	lessons: [
		{
			id: 'school-relationship-family-money-01',
			title: 'Money needs trust',
			description:
				'Learn why Relationships and family money sees trust, honesty, and expectations as part of the financial system.',
			pillar: 'life-decisions',
			schoolsOfThought: ['relationship-family-money'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['money-trust', 'shared-expectations', 'financial-honesty'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'money trust',
				'shared expectations',
				'financial honesty',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Money fights are rarely just about numbers. They are about trust, fear, control, secrecy, pressure, and the stories people carry into the room.',
				},
				{
					type: 'paragraph',
					text: 'Relationships and family money believes money becomes relational when another person depends on your choices. A partner, parent, child, roommate, sibling, or relative can change what a financial decision means.',
				},
				{
					type: 'bullet_list',
					items: [
						'Trust needs visibility',
						'Expectations need words',
						'Shared choices need shared context',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is trying to solve a trust problem with a budget alone.',
				},
				{
					type: 'example',
					text: 'One partner wants to save aggressively because they grew up with instability. The other wants to spend on comfort because they grew up feeling deprived. The fight looks like spending versus saving, but underneath it is safety versus relief.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that money honesty can feel uncomfortable. Naming debt, family pressure, income gaps, fear, or different priorities can feel risky. But silence usually makes the pressure louder later.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens turns Dashboard into a conversation starter. The point is not to judge anyone. The point is to make the current picture visible enough that trust has something real to stand on.',
				},
				{
					type: 'key_takeaway',
					text: 'Shared money works better when trust is built before resentment hardens.',
				},
			],
			audioScript:
				'Relationships and family money starts with a truth that most spreadsheets miss: money is emotional when another person is involved. A budget can show the numbers, but it cannot show the fear behind them. It cannot automatically show why one person panics when savings drops, why another person hates feeling controlled, why someone hides a purchase, or why family support feels like love to one person and pressure to another.\n\nThis lens believes trust is part of the money system. Not a soft extra. A real part. If two people share rent, bills, goals, debt, children, family obligations, or a future plan, then money choices become relationship choices too. A secret credit card balance is not just a balance. It is hidden stress. A partner carrying all the planning is not just organized. They may be quietly exhausted. A parent asking for help is not just an expense. It may carry guilt, culture, love, history, and obligation.\n\nA lot of people try to fix this with pure math. They say, “Let us just make a budget.” A budget can help, but it will not solve a trust problem by itself. If one person feels judged, they may hide. If one person feels ignored, they may push harder. If one person feels unsafe, they may over control. If both people avoid the real conversation, the numbers become a battlefield.\n\nHere is a simple example. One partner wants to save every extra dollar because their childhood taught them that money disappears and safety is fragile. The other partner wants to spend on travel, food, and comfort because their childhood taught them that life is short and deprivation feels terrible. On the surface, they are arguing about a restaurant bill. Underneath, they are arguing about what money is for. Is it protection? Is it joy? Is it control? Is it relief? Is it proof that life is finally better?\n\nNeither person is automatically wrong. That is the point. Relationship money requires translation. You have to say what the number means to you. You have to name the fear, not just the category. You have to explain the goal, not just the rule.\n\nInside Nisk, the Dashboard can be useful because it gives the conversation something concrete. Instead of saying, “You always spend too much,” you can ask, “What does our current picture show? What does our runway look like? What is making us feel unsafe? What would make life feel good without breaking the plan?” That is a better conversation.\n\nThe warning is that honesty needs care. Dumping every fear at once can overwhelm someone. Using numbers as a weapon can damage trust. The goal is not to win the argument. The goal is to build a shared reality. Once the reality is shared, the plan can become calmer.\n\nMoney needs trust because money touches survival, freedom, identity, and love. Treat it with that much respect.',
			flashcards: [
				{
					id: 'school-relationship-family-money-01-fc-1',
					role: 'main_idea',
					front: 'Why does money need trust?',
					back: 'Shared money choices carry fear, history, control, and expectations.',
				},
				{
					id: 'school-relationship-family-money-01-fc-2',
					role: 'watch_out',
					front: 'What does a budget miss?',
					back: 'A budget can miss secrecy, resentment, fear, and emotional meaning.',
				},
				{
					id: 'school-relationship-family-money-01-fc-3',
					role: 'use_it',
					front: 'How do you start?',
					back: 'Make the current picture visible, then name what the numbers mean.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Start with the real picture',
			},
			revision: 1,
		},
		{
			id: 'school-relationship-family-money-02',
			title: 'Family support needs boundaries',
			description:
				'Learn why Relationships and family money treats generosity, caregiving, and obligation as real tradeoffs.',
			pillar: 'life-decisions',
			schoolsOfThought: ['relationship-family-money'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['family-support', 'money-boundaries', 'caregiving-pressure'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'family support',
				'money boundaries',
				'caregiving pressure',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Helping family can be one of the most meaningful uses of money. It can also become one of the fastest ways to lose stability, peace, and future options if nobody names the limits.',
				},
				{
					type: 'paragraph',
					text: 'Relationships and family money believes family support should be treated with both compassion and clarity. Love matters. Culture matters. Caregiving matters. So do runway, bills, debt, savings, and your future self.',
				},
				{
					type: 'bullet_list',
					items: [
						'Name the amount',
						'Name the purpose',
						'Name the limit',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is saying yes from guilt and then paying for it with resentment.',
				},
				{
					type: 'example',
					text: 'Someone sends money home every month, but never counts it as a real line in their plan. They feel proud and stressed at the same time. Once they name the monthly amount, the reason, and the limit, the support becomes more honest.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that boundaries can feel cold, especially in families where sacrifice is expected. But unclear support can create quiet anger. Clear support can protect the relationship from hidden debt, panic, and blame.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects family support to runway. You are not asking whether family matters. You are asking how to support people without quietly destroying the base you stand on.',
				},
				{
					type: 'key_takeaway',
					text: 'Generosity works better when it has a number, a purpose, and a limit.',
				},
			],
			audioScript:
				'Family money is hard because it is not just math. It is love, guilt, culture, history, survival, sacrifice, pride, fear, and responsibility all mixed together. A parent needs help. A sibling is struggling. A relative expects support. A partner has family obligations. A child needs stability. These are not small things. They are human things.\n\nRelationships and family money does not say, “Never help.” That would be too cold and too simple. For many people, helping family is part of who they are. It can be one of the most meaningful uses of money. It can honor sacrifice. It can protect someone vulnerable. It can keep a family stable. But this lens also says support needs boundaries, because money without limits can turn love into resentment.\n\nThe problem usually starts with silence. Someone sends money when asked. Then again. Then again. They do not put it in the budget because it feels emotional, not financial. They do not want to say no because saying no feels selfish. They do not want to explain their own stress because someone else seems to need help more. Over time, their runway shrinks, debt grows, goals stall, and resentment builds. Now the support is still happening, but the relationship feels heavier.\n\nA better version starts by naming the support clearly. What amount can you actually afford? What is the purpose? Is it temporary or ongoing? What is the limit? What happens if the need grows? What are you not willing to cover? These questions can feel uncomfortable, but they protect the truth. They turn vague pressure into a real decision.\n\nFor example, someone might decide they can send $250 per month to help a parent. That number goes into the plan like rent, food, or transportation. It is not hidden. It is not a surprise. It is not handled only through guilt. Another person might decide they can help with one emergency bill but cannot become the backup plan for every future crisis. Someone else might choose to help through time, research, errands, paperwork, or shared planning instead of cash.\n\nThe tradeoff is that boundaries can feel harsh, especially in families where sacrifice is normal. But unclear support can be harsher in the long run. It can create debt, panic, blame, and emotional distance. A clear boundary can sound like: “I love you. I can help with this amount. I cannot go beyond it without risking my own stability.” That is not selfish. That is honest.\n\nInside Nisk, this is where runway matters. If family support is part of your life, it belongs in your real numbers. Look at what the support does to your cash flow. Look at what it does to emergency runway. Look at what it delays. Not to shame the support, but to make the tradeoff visible. When the tradeoff is visible, the decision can become more honest.\n\nFamily support is not just an expense. It is a values decision. But values decisions still need numbers.',
			flashcards: [
				{
					id: 'school-relationship-family-money-02-fc-1',
					role: 'main_idea',
					front: 'What does family support need?',
					back: 'A clear amount, purpose, time frame, and limit.',
				},
				{
					id: 'school-relationship-family-money-02-fc-2',
					role: 'watch_out',
					front: 'What creates resentment?',
					back: 'Saying yes from guilt while hiding the real cost.',
				},
				{
					id: 'school-relationship-family-money-02-fc-3',
					role: 'use_it',
					front: 'How do you protect stability?',
					back: 'Count support in your plan and set honest boundaries.',
				},
			],
			applyAction: {
				type: 'calculateRunway',
				label: 'Check support against runway',
			},
			revision: 1,
		},
		{
			id: 'school-relationship-family-money-03',
			title: 'Shared goals need shared language',
			description:
				'Learn why Relationships and family money cares about agreements before big shared decisions.',
			pillar: 'life-decisions',
			schoolsOfThought: ['relationship-family-money'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['shared-goals', 'money-language', 'agreement-design'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['shared goals', 'money language', 'agreement design'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Shared money goals fail when people use the same words but mean different things. Saving, safety, freedom, success, support, and enough can mean completely different things to two people.',
				},
				{
					type: 'paragraph',
					text: 'Relationships and family money believes shared goals need shared language. Before people combine plans, they need to define what the goal means, who carries what, and what tradeoff they are accepting together.',
				},
				{
					type: 'bullet_list',
					items: [
						'Define the goal',
						'Define each person’s role',
						'Define what you will not sacrifice',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is assuming agreement because nobody objected.',
				},
				{
					type: 'example',
					text: 'A couple says they want to buy a home. One person means stability near family. The other means investment upside and more space. If they do not name the difference, the same goal can create conflict later.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is over talking without deciding. A good conversation should lead to a clearer next step, not endless emotional processing with no plan.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens points to Simulate and reflection. Test the shared choice, then write the agreement in plain language. The goal is not perfection. The goal is fewer surprises.',
				},
				{
					type: 'key_takeaway',
					text: 'Shared goals work better when people define the same words before building the same plan.',
				},
			],
			audioScript:
				'Shared money goals sound simple until you realize two people can use the same word and mean totally different things. One person says “security” and means a large emergency fund. Another says “security” and means owning a home. One person says “freedom” and means retiring early. Another says “freedom” and means taking vacations without guilt. One person says “support family” and means sending money every month. Another means helping only during true emergencies.\n\nThis is why Relationships and family money cares about shared language. The goal is not just to agree on a category. The goal is to understand what the category means. Buying a home, paying off debt, moving, helping parents, having kids, combining accounts, starting a business, or supporting a partner through school are not just financial moves. They are identity moves. They change roles, pressure, timelines, and expectations.\n\nThe dangerous phrase is, “We are on the same page,” when nobody actually read the page out loud. Silence can look like agreement. Avoidance can look like peace. One person may think the plan is obvious. The other may be quietly unsure, scared, or assuming something different. Later, when the payment hits or the sacrifice becomes real, the conflict appears.\n\nTake a couple saying they want to buy a home. One person wants roots near family, a yard, and emotional stability. The other sees a property as a wealth move and wants appreciation, rental potential, or a better long term return. Both say “buy a home,” but they are not chasing the same thing. If they do not name that early, every decision becomes harder. Which city? How much payment? How much risk? How long to stay? What counts as success?\n\nShared language does not mean every conversation has to be deep and dramatic. It can be practical. What are we trying to buy with this decision? Stability, flexibility, status, comfort, time, family support, income upside, or peace? What are we willing to trade? What are we not willing to trade? Who is responsible for what? What would make us pause? What would make us revisit the plan?\n\nThe blind spot is talking forever without deciding. Some people use conversation to avoid action. That is not the point either. A good money conversation should create a clearer next step. Maybe the next step is checking the numbers. Maybe it is setting a limit. Maybe it is waiting six months. Maybe it is testing the decision in Simulate. Maybe it is writing down a shared agreement.\n\nInside Nisk, this lens works well with Simulate because big shared decisions need a stress test. What happens if income changes? What happens if expenses rise? What happens if family support becomes part of the plan? What happens if one person wants flexibility and the other wants roots? The numbers will not settle every emotional question, but they make the tradeoff visible.\n\nShared goals do not need perfect agreement. They need honest agreement. That starts with shared language.',
			flashcards: [
				{
					id: 'school-relationship-family-money-03-fc-1',
					role: 'main_idea',
					front: 'Why do shared goals need language?',
					back: 'People can use the same money words and mean different things.',
				},
				{
					id: 'school-relationship-family-money-03-fc-2',
					role: 'watch_out',
					front: 'What false agreement happens often?',
					back: 'Assuming silence means both people understand and accept the same plan.',
				},
				{
					id: 'school-relationship-family-money-03-fc-3',
					role: 'use_it',
					front: 'What should you define?',
					back: 'The goal, each person’s role, the tradeoff, and the limits.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Test the shared decision',
				scenarioType: 'custom',
			},
			revision: 1,
		},
	],
};
